import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { useAnalysis } from "@/context/AnalysisContext";
import { api } from "@/lib/api";
import { fmt$ } from "@/lib/format";
import { toast } from "sonner";
import { Home as HomeIcon, Trash2, Copy, ExternalLink, Pencil } from "lucide-react";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const { loadFromProperty } = useAnalysis();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!loading && !user) nav("/login?next=/dashboard");
  }, [loading, user, nav]);

  useEffect(() => {
    if (!user) return;
    api.get("/properties").then(r => { setItems(r.data); setBusy(false); }).catch(() => setBusy(false));
  }, [user]);

  const del = async (id) => {
    if (!confirm("Delete this saved home?")) return;
    await api.delete(`/properties/${id}`);
    setItems(items.filter(i => i.property_id !== id));
    toast.success("Deleted");
  };
  const dup = async (id) => {
    try {
      const { data } = await api.post(`/properties/${id}/duplicate`);
      setItems([data, ...items]); toast.success("Duplicated");
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(e.response?.data?.detail || "Upgrade to save more homes.");
        nav("/upgrade?next=/dashboard&reason=save_limit");
      } else {
        toast.error("Could not duplicate");
      }
    }
  };
  const rename = async (item) => {
    const name = prompt("Rename this home", item.nickname);
    if (!name || name === item.nickname) return;
    const { data } = await api.patch(`/properties/${item.property_id}`, { nickname: name });
    setItems(items.map(i => i.property_id === item.property_id ? { ...i, ...data } : i));
    toast.success("Renamed");
  };
  const open = (item) => {
    loadFromProperty(item);  // hydrate the working worksheet
    sessionStorage.setItem("hs_current", JSON.stringify({ inputs: item.inputs, results: item.results }));
    nav("/results");
  };
  const openWizard = (item) => {
    loadFromProperty(item);
    nav("/analyze?step=0");
  };

  return (
    <>
      <Nav />
      <main className="container-hs py-10 md:py-14">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl">My Homes</h1>
            <p className="text-sm text-brand-muted mt-1">
              Saved analyses appear here.
              {user?.entitlement && (
                user.entitlement.plan_type === "premium"
                  ? <span data-testid="dash-plan" className="ml-2 text-brand-accent">· Home Buying Pass · {user.entitlement.days_remaining} days remaining</span>
                  : <span data-testid="dash-plan" className="ml-2">· Free plan · {user.entitlement.saved_count}/{user.entitlement.saved_limit} homes saved</span>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            {user?.entitlement?.plan_type !== "premium" && (
              <button data-testid="dash-upgrade" onClick={() => nav("/upgrade?next=/dashboard")} className="btn-secondary">Unlock 90 Days</button>
            )}
            <Link to="/analyze" className="btn-primary">Analyze a New Home</Link>
          </div>
        </div>
        {busy ? <div className="text-brand-muted">Loading…</div> : items.length === 0 ? (
          <div className="card-hs p-10 text-center">
            <HomeIcon className="mx-auto mb-3 text-brand-muted" />
            <p className="text-brand-muted">You haven't saved any homes yet.</p>
            <Link to="/analyze" className="btn-primary mt-4 inline-flex">Start your first analysis</Link>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {items.map(i => (
              <div key={i.property_id} data-testid={`home-${i.property_id}`} className="card-hs p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display text-lg text-brand-ink">{i.nickname}</div>
                    <div className="text-xs uppercase tracking-wide text-brand-muted mt-1">{i.financing_type}</div>
                    {i.updated_at && <div className="text-[11px] text-brand-muted mt-1" data-testid={`updated-${i.property_id}`}>Last updated {new Date(i.updated_at).toLocaleString()}</div>}
                  </div>
                  <div className="flex gap-1">
                    <button data-testid={`rename-${i.property_id}`} onClick={() => rename(i)} className="p-1.5 text-brand-muted hover:text-brand-ink" aria-label="Rename"><Pencil size={14}/></button>
                    <button data-testid={`dup-${i.property_id}`} onClick={() => dup(i.property_id)} className="p-1.5 text-brand-muted hover:text-brand-ink" aria-label="Duplicate"><Copy size={14}/></button>
                    <button data-testid={`del-${i.property_id}`} onClick={() => del(i.property_id)} className="p-1.5 text-brand-muted hover:text-brand-danger" aria-label="Delete"><Trash2 size={14}/></button>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div><div className="text-xs text-brand-muted">Monthly</div><div className="font-semibold text-brand-ink">{fmt$(i.results?.monthly_total)}</div></div>
                  <div><div className="text-xs text-brand-muted">Max cash</div><div className="font-semibold text-brand-accent">{fmt$(i.results?.max_transaction_cash)}</div></div>
                  <div><div className="text-xs text-brand-muted">Price</div><div>{fmt$(i.results?.purchase_price)}</div></div>
                  <div><div className="text-xs text-brand-muted">Score</div><div>{i.results?.score_available ? `${Math.round(i.results?.score || 0)} / 100` : "—"}</div></div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <button data-testid={`open-${i.property_id}`} onClick={() => open(i)} className="btn-secondary text-sm py-2 px-3"><ExternalLink size={14}/> Open</button>
                  <button data-testid={`edit-${i.property_id}`} onClick={() => openWizard(i)} className="btn-secondary text-sm py-2 px-3"><Pencil size={14}/> Continue editing</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
